import type { UserBond } from './index'

export interface PositionRow {
  bond: UserBond
  certificateOwner: `0x${string}` | null
  /** Contract-reported claimable principal — authoritative, unlike a client estimate. */
  claimable: bigint
}

interface ContractReadResult {
  status: string
  result?: unknown
}

/**
 * Decode the repeated [bond, certificate owner, claimable] multicall groups.
 *
 * Keeping this order explicit prevents an address from being mistaken for the
 * claim tuple (or vice versa), which otherwise only fails once a wallet owns a bond.
 */
export function decodePositionRows(
  ids: readonly bigint[],
  results: readonly ContractReadResult[],
): PositionRow[] {
  const rows: PositionRow[] = []

  for (let i = 0; i < ids.length; i++) {
    const bondRes = results[i * 3]
    const ownerRes = results[i * 3 + 1]
    const claimRes = results[i * 3 + 2]
    if (bondRes?.status !== 'success' || !Array.isArray(bondRes.result)) continue

    const t = bondRes.result as unknown as [
      bigint, bigint, `0x${string}`, bigint, bigint, bigint, bigint, bigint, bigint, boolean,
    ]
    const claimTuple = claimRes?.status === 'success' && Array.isArray(claimRes.result)
      ? claimRes.result
      : null
    const certificateOwner =
      ownerRes?.status === 'success' && typeof ownerRes.result === 'string'
        ? (ownerRes.result as `0x${string}`)
        : null

    rows.push({
      bond: {
        id: t[0],
        offeringId: t[1],
        owner: t[2],
        payoutAmount: t[3],
        paymentPaid: t[4],
        startTime: t[5],
        lastClaimTime: t[6],
        claimedPrincipal: t[7],
        claimedRewards: t[8],
        fullyClaimed: t[9],
      },
      claimable: typeof claimTuple?.[0] === 'bigint' ? claimTuple[0] : 0n,
      certificateOwner,
    })
  }

  return rows.sort((a, b) => Number(b.bond.id - a.bond.id))
}
